import { Card, CircularProgress, Stack, Typography } from "@mui/material"
import { FC, useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import { Client } from "shared/client"
import { FeatureEnum } from "shared/featureData"
import { MblsConnectorClient } from "src/features/mblsConnector/mblsConnector"
import useFeatures from "src/features/things/useFeatures"
import { useCurrentTenant } from "src/hooks/useCurrentTenant"

import { ModeContext } from "src/ModeContext"
import { OsCreateMblsClientDialog } from "./OsCreateMblsClientDialog"
import { OsCreateSl2ClientDialog } from "./OsCreateSl2ClientDialog"
import { OsReservationClientMblsSelectorItem } from "./OsReservationClientMblsSelectorItem"
import { OsReservationClientSelectorItem } from "./OsReservationClientSelectorItem"
import { Sl2Btn } from "./Sl2Btn"

type OsReservationClientSelectorProps = {
  mblsClients: MblsConnectorClient[]
  clients: Client[]
  isLoading: boolean
  onSelectMbslClient: (client: MblsConnectorClient) => void
  onSelectClient: (client: Client) => void
  onRefetchMblsClient?: () => void
  onRefetchClient?: () => void
}

// eslint-disable-next-line
export const OsReservationClientSelector: FC<
  OsReservationClientSelectorProps
> = ({
  mblsClients,
  clients,
  isLoading,
  onSelectMbslClient,
  onSelectClient,
  onRefetchMblsClient,
  onRefetchClient,
}) => {
  const { t } = useTranslation()

  const nbClients = clients.length + mblsClients.length

  const [isCreateClientDialogOpen, setIsCreateClientDialogOpen] =
    useState(false)

  const mode = useContext(ModeContext)

  /**
   * LOCKER-410 `DoD4`/`DoD5`: the create-client control is gated on a real
   * per-tenant Feature, resolved through the same `assignment ?? default`
   * precedence the backend enforces (`shared/featureResolution`). It replaces
   * a per-browser localStorage flag that was read once at MODULE load — so it
   * could not even be flipped without a page reload, let alone by anyone other
   * than the operator sitting at that browser.
   */
  const { tenant } = useCurrentTenant()
  const { canUseFeature } = useFeatures(tenant?.id)
  const canCreateMblsClient = canUseFeature(FeatureEnum.CreateMblsClient)

  return (
    <Stack data-ui="UI-SL2W-051"
      spacing={1}
      direction="column"
      className={`uf-clients-search-result`}
      sx={{
        p: 2,
        // height: "50px",
        // maxHeight: "50px",
        maxHeight: "calc(100vh - 40px)",
        bgcolor: "#FAFBFB",
        width: "400px",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body1" fontWeight="bold">
          {`${t("pickClientToAutoCompleteForm")}`}
        </Typography>
        {isLoading && <CircularProgress size={"2rem"} />}
      </Stack>
      {!isLoading && (
        <Typography variant="caption">
          {`${nbClients} ${t(nbClients > 1 ? "foundClients" : "foundClient")}`}
        </Typography>
      )}
      <Stack
        direction="column"
        spacing={1}
        sx={{
          overflow: "auto",
        }}
      >
        {clients.map((c) => (
          <OsReservationClientSelectorItem
            key={c.id}
            id={c.id || "-1"}
            firstName={c.firstName || ""}
            lastName={c.lastName || ""}
            email={c.email || ""}
            phone={c.phone || ""}
            onSelectClient={() => onSelectClient(c)}
          />
        ))}
        {mblsClients.map((c) => (
          <OsReservationClientMblsSelectorItem
            key={c.id}
            client={c}
            onSelectClient={() => onSelectMbslClient(c)}
          />
        ))}
        {/* card when clients */}
        {nbClients === 0 && !isLoading && (
          <Card
            className={`uf-no-search-result`}
            sx={{
              p: 1,
              // textAlign: "center",
              minHeight: "100px",
              // vertical center
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            variant="outlined"
          >
            <Stack direction="column" spacing={1} alignItems="center">
              <Typography variant="body1" fontWeight={"bold"}>
                {t("noClientFound")}
              </Typography>
              <Typography variant="body2">
                {t("noClientFoundDescription")}
              </Typography>
            </Stack>
          </Card>
        )}
      </Stack>
      {mode === "embedded" && canCreateMblsClient && (
        <Stack direction="column" spacing={1}>
          <Sl2Btn
            variant="outlined"
            onClick={() => setIsCreateClientDialogOpen(true)}
          >
            {t("createClient")}
          </Sl2Btn>
          <OsCreateMblsClientDialog
            open={isCreateClientDialogOpen}
            onClose={() => setIsCreateClientDialogOpen(false)}
            onSuccess={() => {
              onRefetchMblsClient && onRefetchMblsClient()
              setIsCreateClientDialogOpen(false)
            }}
          />
        </Stack>
      )}
      {mode !== "embedded" && (
        <Stack direction="column" spacing={1}>
          <Sl2Btn
            variant="outlined"
            onClick={() => setIsCreateClientDialogOpen(true)}
          >
            {t("createClient")}
          </Sl2Btn>
          <OsCreateSl2ClientDialog
            open={isCreateClientDialogOpen}
            onClose={() => setIsCreateClientDialogOpen(false)}
            onSuccess={() => {
              onRefetchClient && onRefetchClient()
              setIsCreateClientDialogOpen(false)
            }}
          />
        </Stack>
      )}
    </Stack>
  )
}
